import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from "react-router-dom";
import { increment, updateDoc, doc, getDoc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

import customBackgroundByUser from '../../store/customBackground'; // 임시
import styleClasses from './css/MusicardDetail.module.scss';

import LeftMenu from './LeftMenu';
import CDPlayer from './CDPlayer';
import Loading from '../Loading';
import MusicardPopup from './MusicardPopup';
import MusicArchive from './MusicArchive';
import PositionSkill from './PositionSkill';
import Activity from './Activity';
import Contact from './Contact';
import Members from './Members'; // ✅ Members import 추가
import QRCode from './QRCode';
import Share from './Share';
import SEO from '../SEO';

const MusicardDetail = ({ isTablet }) => {
  const location = useLocation();
  const { id, menu } = useParams();
  const [musician, setMusician] = useState(location.state?.musician || null); // Link로 전달된 musician 데이터
  const [styleData, setStyleData] = useState(null);
  const [loading, setLoading] = useState(!musician); // musician이 없으면 로딩 시작
  const engName = musician?.eng || '';

  // 모달
  const [modalOpen, setModalOpen]         = useState(menu && menu !== '' ? true : false);
  const [archiveModal, setArchiveModal]   = useState(menu === 'music-archive' ? true : false);
  const [positionModal, setPositionModal] = useState(menu === 'position-skill' ? true : false);
  const [activityModal, setActivityModal] = useState(menu === 'activity' ? true : false);
  const [contactModal, setContactModal]   = useState(menu === 'contact' ? true : false);
  const [membersModal, setMembersModal]   = useState(menu === 'members' ? true : false); // ✅ Members 모달 상태 추가
  const [qrCodeModal, setQrCodeModal]     = useState(menu === 'qr-code' ? true : false);
  const [shareModal, setShareModal]       = useState(menu === 'share' ? true : false);

  // 커스텀
  const [customHtml, setCustomHtml] = useState('');
  const [customCss, setCustomCss]   = useState('');
  const [customJs, setCustomJs]     = useState('');

  const hasSavedVisit = useRef(false); // 중복 저장 방지
  // ✅ URL에서 ref 파라미터 확인
  const searchParams = new URLSearchParams(location.search);
  const refSource = searchParams.get("ref"); // ref 값 가져오기

  // 커스텀 - 뮤지션 이름
  const [customNameHtml, setCustomNameHtml] = useState('');
  const [customNameCss, setCustomNameCss]   = useState('');

  // 커스텀 CSS, JS 초기화
  const initializeStyleAndScript  = () => {
    const existingStyle1 = document.querySelector('#custom-style');
    if (existingStyle1) { existingStyle1.remove(); }
    const existingStyle2 = document.querySelector('#custom-name-style');
    if (existingStyle2) { existingStyle2.remove(); }
    const existingScript = document.querySelector('#custom-script');
    if (existingScript) { existingScript.remove(); }
  };

  const [hitCount, setHitCount] = useState({ todays_hit: 0, total_hit: 0 });

  // 방문 기록 저장 (Today Hit, Total Hit)
  // 5분 이내 중복 방지
  const canCountHit = (id) => {
    const key = `hit-time-${id}`;
    const now = Date.now();
    const last = localStorage.getItem(key);
    if (last && now - Number(last) < 5 * 60 * 1000) return false;
    localStorage.setItem(key, now);
    return true;
  };

  useEffect(() => {
    const updateHitCounts = async () => {
      if (!canCountHit(id)) return;

      try {
        const docRef = doc(db, 'hit_manage', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          await updateDoc(docRef, {
            todays_hit: increment(1),
            total_hit: increment(1)
          });
        } else {
          await setDoc(docRef, {
            todays_hit: 1,
            total_hit: 1
          });
        }
      } catch (error) {
        console.error('히트카운트 업데이트 오류:', error);
      }
    };

    updateHitCounts();
  }, [id]);


  useEffect(() => {
    const fetchHitCounts = async () => {
      try {
        const docRef = doc(db, 'hit_manage', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setHitCount(docSnap.data());
        }
      } catch (error) {
        console.error('히트카운트 불러오기 오류:', error);
      }
    };

    fetchHitCounts();
  }, [id]);

  // Firestore에서 musician 데이터 가져오기
  useEffect(() => {
    if (!musician) {
      const fetchMusician = async () => {
        try {
          const docRef = doc(db, 'musicians', id);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            setMusician(docSnap.data());            
          } else {
            console.error('Musician not found');
          }
        } catch (error) {
          console.error('Error fetching musician data:', error.message);
        }
      };
      fetchMusician();
    }
  }, [id, musician]);

  // ✅ Firestore에 방문 기록 저장
  useEffect(() => {
    if (hasSavedVisit.current) return; // 중복 실행 방지
    if (!refSource) return; // ref 파라미터가 없으면 저장 X
    hasSavedVisit.current = true; // 실행 체크

    const saveVisitData = async () => {
      try {
        // Firestore 경로: hit_manage → SNS_redirects → musicianId
        const visitRef = doc(collection(db, "hit_manage", "SNS_redirects", id));

        await setDoc(visitRef, {
          timestamp: serverTimestamp(),
          userAgent: navigator.userAgent, // 사용자의 기기 정보
          referrer: refSource, // ref 파라미터 값 저장
          fullUrl: window.location.href, // 방문한 전체 URL
        }, { merge: true });

        console.log("SNS 방문 기록 저장 완료! Referrer:", refSource);
      } catch (error) {
        console.error("SNS 방문 기록 저장 실패: ", error);
      }
    };

    saveVisitData();
  }, [id, refSource]); // ref 값이 바뀌면 실행

  // Firestore에서 style 데이터 가져오기
  useEffect(() => {
    const fetchStyleData = async () => {
      try {
        const styleDocRef = doc(db, `musicians/${id}/style`, 'main');
        const styleDocSnap = await getDoc(styleDocRef);
  
        if (styleDocSnap.exists()) {
          const data = styleDocSnap.data();
          setStyleData(data);
          
          let styleCSS = "";

          if (data.font) {
            const fontName = extractFontName(data.font);
            if (fontName) {
              // 폰트 등록
              if (data.font.includes('fonts.googleapis.com')) {
                styleCSS += `@import url('${data.font}');\n`;
              } else {
                styleCSS += `
                  @font-face {
                    font-family: '${fontName}';
                    src: url('${data.font}') format('${
                      data.font.endsWith('.woff2')
                        ? 'woff2'
                        : data.font.endsWith('.woff')
                        ? 'woff'
                        : 'truetype'
                    }');
                    font-weight: normal;
                    font-style: normal;
                  }
                `;
              }
              // data.fontcolor가 있으면 그 값, 없으면 기본 "#fff" 적용
              const fontColor = data.fontcolor || "#fff";
              styleCSS += `body { .musicardContainer * { font-family: '${fontName}', sans-serif; color: ${fontColor}; } }\n`;
            } else {
              // data.font가 있으나, 폰트 이름 추출에 실패한 경우
              const fontColor = data.fontcolor || "#fff";
              styleCSS += `body { .musicardContainer * { color: ${fontColor}; } }\n`;
            }
          } else if (data.fontcolor) {
            // data.font가 없지만 fontcolor만 있는 경우
            styleCSS += `body { .musicardContainer * { color: ${data.fontcolor}; } }\n`;
          } else {
            // 둘 다 없는 경우, 기본적으로 흰색을 적용
            styleCSS += `body { .musicardContainer * { color: #fff; } }\n`;
          }
          
          if (styleCSS) {
            const styleElement = document.createElement('style');
            styleElement.innerHTML = styleCSS;
            document.head.appendChild(styleElement);
          }
        } else {
          console.error('Style data not found');
        }

        // 커스텀 영역
        const customBackground = customBackgroundByUser.find(
          (custom) => custom.id === id
        );
        
        if (customBackground) {
          initializeStyleAndScript();

          setCustomNameHtml(customBackground.nameHtml);
          setCustomNameCss(customBackground.nameStyle);
          setCustomHtml(customBackground.html || "");
          setCustomCss(customBackground.css || "");
          setCustomJs(customBackground.js || "");
          
          // Apply CSS
          if (customCss) {
            const styleElement = document.createElement("style");
            if (styleElement) {
              styleElement.id = "custom-style";
              styleElement.innerHTML = customBackground.css;
              document.head.appendChild(styleElement);
            }
          }
          
          // Apply CSS
          if (customNameCss) {
            const styleElement = document.createElement("style");
            if (styleElement) {
              styleElement.id = "custom-name-style";
              styleElement.innerHTML = customBackground.nameStyle;
              document.head.appendChild(styleElement);
            }
          }

          // Apply JS
          if (customJs) {
            const scriptElement = document.createElement('script');
            if (scriptElement) {
              scriptElement.id = 'custom-script';
              scriptElement.textContent = customJs;
              document.body.appendChild(scriptElement);
            }
          }
        } else {
          initializeStyleAndScript();
        }

      } catch (error) {
        console.error('Error fetching style data:', error.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchStyleData();

    return () => {
      ['#custom-script', '#custom-style', '#custom-name-style'].forEach((selector) => {
        const el = document.querySelector(selector);
        if (el) el.remove();
      });
    };

  }, [id, customHtml, customCss, customJs, customNameHtml, customNameCss, menu, modalOpen]);
  
  // Google Fonts URL에서 폰트 이름 추출 함수
  //const extractFontName = (fontUrl) => {
  //  const match = fontUrl.match(/family=([^:&]+)/);
  //  return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
  //};
  
  // 폰트 이름 추출 함수
  const extractFontName = (fontUrl) => {
    if (!fontUrl) return null;

    // 1. Google Fonts URL 형식 처리
    const googleFontsMatch = fontUrl.match(/family=([^:&]+)/);
    if (googleFontsMatch) {
      return decodeURIComponent(googleFontsMatch[1].replace(/\+/g, ' '));
    }

    // 2. 일반 URL에서 파일 이름 추출
    const urlSegments = fontUrl.split('/');
    const fileName = urlSegments[urlSegments.length - 1]; // 마지막 경로 (파일명)
    
    // 확장자 제거 (.woff, .ttf 등)
    const fontName = fileName.split('.')[0];
    return fontName || null;
  };

  if (loading) return <Loading />;

  
  if (!musician || !styleData) return <p>Data not available</p>;
  // const musicianUrl = `${window.location.origin}/musicard/${id}`; // 현재 뮤지션 페이지 URL
  
  // SEO
  const browserLanguage =  navigator.language === 'ko' ? 'ko' : 'en';
  let musicianDescription = '';
  if (musician.genres) {
    const genreString = (musician.genres).join(', ');
   
    musicianDescription = genreString;
  }
  if (musician.positions) {
    const positionString  = (musician.positions).join(', ');
   
    if (musicianDescription !== '') musicianDescription += ', ' + positionString;
    else                            musicianDescription = positionString;
  }

  return (
    <>
      <SEO
        //title={`${ browserLanguage === 'ko' ? musician.name  : musician.eng !== '' ? musician.eng : musician.name } - ${ musician.positions?.join(", ") }`}
        title={`${ browserLanguage === 'ko' ? musician.name  : engName !== '' ? engName : musician.name } - ${ musician.positions?.join(", ") }`}
        description={`${ browserLanguage === 'ko' ? '뮤지션'  : 'Musician' } - ${ musicianDescription }`}
        image={ musician.profileImage }
        keywords={ musicianDescription }
      />
      <div
        className={`musicardContainer ${ styleClasses.musicardContainer }`}
        style={{ background: styleData.backgroundColor || '#0C0C12' }}
      >
        {/* SEO */}
        <h1 style={{ display: 'none' }}>{`${ browserLanguage === 'ko' ? musician.name  : musician.eng !== '' ? musician.eng : musician.name } - ${ musician.positions?.join(", ") }, ${ musician.genres?.join(", ") }`}</h1>
        <div className={`customBackground ${ styleClasses.customBackground }`} dangerouslySetInnerHTML={{ __html: customHtml }}></div>

        {/* LeftMenu에 styleData 전달 */}
        <LeftMenu
          musician={ styleData }
          isGroup={ Boolean(musician.positions?.includes("Group/Band")) } // ✅ Boolean()으로 명확하게 변환
          setModalOpen={ setModalOpen }
          setArchiveModal={ setArchiveModal }
          setPositionModal={ setPositionModal }
          setActivityModal={ setActivityModal }
          setContactModal={ setContactModal }
          setMembersModal={ setMembersModal } // ✅ 추가됨
          isTablet={ isTablet }
        />
      
        <div
          className={ styleClasses.musicardDetail }
          //style={{
          //  backgroundImage: `url(${musician.profileImage})`,
          //}}
        >
          <div className={ styleClasses.nameContainer }>

            {
              customNameHtml === ''
              ?
                <h1 className={`name ${ styleClasses.name }`} dangerouslySetInnerHTML={{ __html: isTablet ?  `${ musician.name } ${ musician.eng ? `</br>${ musician.eng }` : '' }` : `${ musician.name } ${ musician.eng ?? '' }` }} />
              :
                <h1 className={`name ${ styleClasses.name }`} dangerouslySetInnerHTML={{ __html: customNameHtml !== '' && customNameHtml.replaceAll('[MUSICIAN_NAME]', isTablet ? `${ musician.name }\r\n${ musician.eng ?? '' }` : `${ musician.name } ${ musician.eng ?? '' }`) }}></h1>
            }
          </div>

          <CDPlayer videoId={ styleData.videoId || 'q3gKI9o8QhM' } videoTitle={ styleData.videoTitle } thumbnail={ styleData.CDThumbnail } styleData={ styleData.dark } styleClasses={ styleClasses } />
          {/* Music Links */}
          <div className={ styleClasses.musicLinks }>
            {styleData.musicLinks &&
            Object.entries(styleData.musicLinks).map(([key, url]) => {
              const baseKey = key.replace(/\s*\d+$/, '').toLowerCase(); // 숫자 제거 + 소문자 변환
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" title={key}>
                  <img
                    src={`/assets/icons/${baseKey}-icon${styleData.dark ? '_1' : ''}.png`}
                    alt={key}
                    onError={(e) => {
                      e.target.onerror = null; // 무한루프 방지
                      e.target.src = `/assets/icons/etc-icon${styleData.dark ? '_1' : ''}.png` // fallback 이미지
                    }}
                  />
                </a>
              );
            })}
          </div>
          {/* SNS Links */}
          <div className={ styleClasses.snsLinks }>
            {styleData.sns &&
            Object.entries(styleData.sns).map(([key, url]) => {
              const baseKey = key.replace(/\s*\d+$/, '').toLowerCase(); // 숫자 제거 + 소문자 변환
              return (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer" title={key}>
                  <img
                    src={`/assets/icons/${baseKey}-icon${styleData.dark ? '_1' : ''}.png`}
                    alt={key}
                    onError={(e) => {
                      e.target.onerror = null; // 무한루프 방지
                      e.target.src = `/assets/icons/etc-icon${styleData.dark ? '_1' : ''}.png` // fallback 이미지
                    }}
                  />
                </a>
              );
            })}
            {/*styleData.qrimg && (  
              <a key='QR-code' href={ styleData.qrimg } target="_blank" rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  setShareModal(false); setQrCodeModal(true); setArchiveModal(false); setPositionModal(false); setActivityModal(false); setContactModal(false); setModalOpen(true);
                }}
              >
                <img src={ styleData.qrimg } alt='QR-code' />
              </a>
            )*/}
            {/* 링크 복사 아이콘 */}
            <a href="/" className="copyBox"
              onClick={(e) => {
                e.preventDefault();
                setShareModal(true); setQrCodeModal(false); setArchiveModal(false); setPositionModal(false); setActivityModal(false); setContactModal(false); setModalOpen(true);
              }}
            >
              <img src={`/assets/icons/share-icon${ styleData.dark ? '_1' : '' }.png`}  alt="Copy link" />
            </a>
          </div>
          
          {/* 🔥 히트카운트 표시 추가 */}
          {hitCount.total_hit !== undefined && (
            <div className={styleClasses.hitCountWrapper}>
              <div className={styleClasses.hitCard}>
                <div className={styleClasses.hitLabel}>TODAY</div>
                <div className={styleClasses.hitValue}>{hitCount.todays_hit}</div>
              </div>
              <div className={styleClasses.hitCard}>
                <div className={styleClasses.hitLabel}>TOTAL</div>
                <div className={styleClasses.hitValue}>{hitCount.total_hit}</div>
              </div>
            </div>
          )}

          {/* 장르 및 포지션 */}
          <div className={ styleClasses.infoSection }>
            <div className={ styleClasses.genres }>{musician.genres?.join(" · ")}</div>
            <div className={styleClasses.positions}>
              {musician.positions?.join(" · ")}
            </div>
          </div>
        </div>

        {/* 팝업 영역 */}
        {
          modalOpen
          &&
          
          <MusicardPopup
          id={ id }
          setModalOpen={ setModalOpen }
          closeStyle={
            archiveModal ? 'music-archive'
            :
            positionModal ? 'position-skill'
            :
            activityModal ? 'activity'
            :
            contactModal ? 'contact'
            :
            membersModal ? 'member'
            :
            qrCodeModal ? 'qrCode'
            :
            shareModal ? 'share'
            : ''
          }
          element={ 
            archiveModal ? <MusicArchive />
            :
            positionModal ? <PositionSkill />
            :
            activityModal ? <Activity />
            :
            contactModal ? <Contact />
            :
            membersModal ? <Members /> 
            :
            qrCodeModal ? <QRCode image={ styleData.qrimg } name={ musician.name } />
            :
            shareModal ? <Share url={`${window.location.origin}/musicard/${id}`} musician={ musician } image={ styleData.qrimg } />
            :
            null
          }
          />
        }
      </div>
    </>
  );
};

export default MusicardDetail;
